/* eslint-disable no-console */

import {register} from 'register-service-worker'

// Installed PWAs rarely navigate, so the browser almost never re-fetches
// service-worker.js on its own. Poll for it explicitly.
const SW_UPDATE_INTERVAL_MS = 30 * 60 * 1000

// Covers deploys that only touch app_template/index.html: the SW script
// itself is byte-identical, so the browser sees no update. We compare the
// running app-version against the one currently served in index.html.
const VERSION_CHECK_INTERVAL_MS = 15 * 60 * 1000

let reloading = false

// controllerchange and the version check can fire near-simultaneously.
const reloadOnce = () => {
  if (reloading) return
  reloading = true
  window.location.reload()
}

const getRunningAppVersion = () => {
  const meta = document.querySelector('meta[name="app-version"]')
  return meta ? meta.getAttribute('content') : null
}

// Fetch index.html bypassing both the HTTP cache and the SW cache, then
// compare its app-version meta against the one this page booted with.
const checkAppVersion = async registration => {
  try {
    const response = await fetch(window.location.pathname, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {'Cache-Control': 'no-cache'}
    })
    if (!response || !response.ok) return
    const html = await response.text()
    const match = html.match(/<meta[^>]+name=["']app-version["'][^>]+content=["']([^"']+)["']/i)
    if (!match) return
    const remoteVersion = match[1]
    const localVersion = getRunningAppVersion()
    if (localVersion && remoteVersion && remoteVersion !== localVersion) {
      console.log(`[SW] app-version changed (${localVersion} -> ${remoteVersion})`)
      if (registration) {
        try {
          await registration.update()
        } catch (e) {
          /* ignore */
        }
        // A new SW is already installing — controllerchange will reload
        // once it takes over, with the fresh precache in place. A manual
        // reload now would race it and serve old assets.
        if (registration.installing || registration.waiting) return
      }
      reloadOnce()
    }
  } catch (e) {
    // Offline or transient failure — retry next interval.
  }
}

// Defensive nudge for a worker stuck in `waiting`. install already calls
// skipWaiting(), but some browsers defer it while fetches are in flight.
const activateWaitingWorker = registration => {
  const waiting = registration && registration.waiting
  if (waiting) {
    waiting.postMessage({type: 'SKIP_WAITING'})
  }
}

// register-service-worker's `updated` callback checks registration.waiting,
// but sw.js calls skipWaiting() in install, so the worker skips the waiting
// phase entirely and the built-in reload path no-ops. controllerchange is
// the reliable signal that a new SW has taken control.
if ('serviceWorker' in navigator) {
  // No previous controller means this is the first claim after install,
  // not an update — don't reload.
  let hadController = !!navigator.serviceWorker.controller
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!hadController) {
      hadController = true
      return
    }
    // Reloading a backgrounded page breaks iOS swipe-back navigation and
    // confuses bfcache restores. Wait until the user actually returns.
    if (document.visibilityState === 'hidden') {
      const onVisible = () => {
        if (document.visibilityState !== 'visible') return
        document.removeEventListener('visibilitychange', onVisible)
        reloadOnce()
      }
      document.addEventListener('visibilitychange', onVisible)
      return
    }
    console.log('[SW] controller changed, reloading')
    reloadOnce()
  })
}

register(`${process.env.BASE_URL}service-worker.js`, {
  ready(registration) {
    console.log('App is served from the service worker cache.')

    setInterval(() => {
      registration.update().catch(() => {})
    }, SW_UPDATE_INTERVAL_MS)

    setInterval(() => checkAppVersion(registration), VERSION_CHECK_INTERVAL_MS)

    // Check on resume — the user expects fresh content and tolerates a
    // reload better at this moment.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return
      registration.update().catch(() => {})
      checkAppVersion(registration)
    })
    window.addEventListener('focus', () => {
      registration.update().catch(() => {})
    })
  },
  registered(registration) {
    console.log('Service worker registered.')
    // Run an immediate check so a returning user isn't stuck on a stale
    // build for a whole interval.
    registration.update().catch(() => {})
    checkAppVersion(registration)
  },
  cached() {
    console.log('Content cached for offline use.')
  },
  updatefound(registration) {
    console.log('Downloading new version...')
    const installing = registration.installing
    if (!installing) return
    installing.addEventListener('statechange', () => {
      if (installing.state === 'installed' && navigator.serviceWorker.controller) {
        activateWaitingWorker(registration)
      }
    })
  },
  updated(registration) {
    console.log('New version available.')
    // Activate the waiting worker; the controllerchange listener above
    // will handle the reload.
    activateWaitingWorker(registration)
  },
  offline() {
    console.log('No connection — running in offline mode.')
  },
  error(error) {
    console.error('Service worker registration failed:', error)
  }
})
